import React, { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import axios from "axios";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import MDBox from "components/MDBox";
import TypeBadge from "components/ReportUI/Typebadge";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { journeyReportEndpiont } from "config";
import JourneyReportTable from "./journeyReportTable";
import CircularProgress from "@mui/material/CircularProgress";

function JourneyLogs() {
  const [journeyData, setJourneyData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [loading, setLoading] = useState(false);  // ⬅️ loading state

  const [date, setDate] = useState(new Date());

  // Snackbar handlers
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // Table columns
  const columns = [
    { Header: "Date", accessor: "dateRange", width: "10%" },
    { Header: "Vehicle Number", accessor: "thingId", width: "15%" },
    { Header: "Journey Name", accessor: "journeyName", width: "20%" },
    { Header: "Journey Type", accessor: "journeyType", width: "10%" ,
      Cell: ({ value }) => (
        <TypeBadge
          value={value}
          config={{}}
          defaultConfig={{ color: "#1E40AF", bg: "#E9F1FF", icon: null }}
        />
      ),
    },
    { Header: "Journey Time", accessor: "timings", width: "10%" },
    { Header: "ETA Alerts", accessor: "etaCount", width: "10%" },
    { Header: "GeoFence Alerts", accessor: "geofenceCount", width: "10%" },
    { Header: "Number of Stops", accessor: "stopCount", width: "10%" },
    { Header: "Start Time", accessor: "startTime", width: "10%" },
    { Header: "End Time", accessor: "endTime", width: "10%" },
  ];

  const fetchLogs = async () => {
    const token = localStorage.getItem("token");
    setLoading(true); // ⬅️ show loader

    try {
      const data = JSON.stringify({
        date: date.toISOString().split("T")[0],
        zone: "Asia/Kolkata",
      });

      const config = {
        method: "post",
        url: journeyReportEndpiont,
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data,
      };

      const response = await axios.request(config);

      const formattedRows = response.data.things.map((journey) => ({
        dateRange: journey.dateRange,
        thingId: journey.thingId,
        journeyName: journey.journeyName,
        journeyType: journey.journeyType,
        timings: journey.timings,
        etaCount: journey.etaCount || 0,
        geofenceCount: journey.geofenceCount || 0,
        stopCount: journey.stopCount || 0,
        startTime: journey.startTime,
        endTime: journey.endTime,
      }));

      setJourneyData(formattedRows);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading journey logs");
      openErrorSB();
    } finally {
      setLoading(false); // ⬅️ hide loader
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (journeyData.length === 0) {
      setErrorMessage("No data to export");
      openErrorSB();
      return;
    }

    const headers = [
      "Date",
      "Vehicle Number",
      "Journey Name",
      "Journey Type",
      "Journey Time",
      "ETA Alerts",
      "GeoFence Alerts",
      "Number of Stops",
      "Start Time",
      "End Time",
    ];

    const rows = journeyData.map((row) =>
      [
        row.dateRange,
        row.thingId,
        row.journeyName,
        row.journeyType,
        row.timings,
        row.etaCount,
        row.geofenceCount,
        row.stopCount,
        row.startTime,
        row.endTime,
      ].join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `JourneyLogs_${date.toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = () => {
    setDate(new Date());
    setJourneyData([]);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Date Picker */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Date"
                        value={date}
                        onChange={(newValue) => setDate(newValue)}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth size="small" />
                        )}
                      />
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={12} md="auto">
                      <MDBox display="flex" gap={1}>
                        <MDButton
                          variant="gradient"
                          color="info"
                          size="small"
                          onClick={fetchLogs}
                          sx={{ minWidth: "100px" }}
                          disabled={loading}
                        >
                          {loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            "LOAD"
                          )}
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={handleClear}
                          sx={{ minWidth: "100px" }}
                        >
                          CLEAR
                        </MDButton>
                        <DownloadExcelButton label="CSV" onClick={exportToCSV} disabled={loading} />
                      </MDBox>
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {/* Error alert */}
              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              {/* DataTable or Loader */}
              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <CircularProgress />
                ) : (
                  <JourneyReportTable
                    table={{ columns, rows: journeyData }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Snackbars */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Journey Logs"
        content="Data Loaded Successfully"
        dateTime="Just now"
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="Journey Logs"
        content="Failed to load logs. Please try again."
        dateTime="Just now"
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default JourneyLogs;
